import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useCreateOrder, useValidateCoupon } from "../../backend/hooks";
import { clearCart } from "../../redux/cartSlice";
import { lineLabel, getCartShipping } from '../../utils/variants';
import WhatsAppButton from "../../common/whatsapp";
import FreeDeliveryCelebration from "./freeDeliveryCelebration";

// Delivery is free inside these cities. The backend applies the same rule when
// it stores the order, so this list must match FREE_SHIPPING_CITIES there.
const FREE_DELIVERY_CITIES = ["lahore"];
const isFreeDeliveryCity = (city) => FREE_DELIVERY_CITIES.includes(String(city || "").trim().toLowerCase());

// ✅ List of Pakistani cities (major + all provinces)
const PAKISTAN_CITIES = [
  "Abbottabad",
  "Bahawalpur",
  "Chakwal",
  "Dera Ghazi Khan",
  "Faisalabad",
  "Gujranwala",
  "Gujrat",
  "Hyderabad",
  "Islamabad",
  "Jacobabad",
  "Jamshoro",
  "Jhelum",
  "Karachi",
  "Kasur",
  "Khanewal",
  "Khuzdar",
  "Lahore",
  "Larkana",
  "Mardan",
  "Mianwali",
  "Mirpur Khas",
  "Multan",
  "Muridke",
  "Muzaffarabad",
  "Nawabshah",
  "Okara",
  "Peshawar",
  "Quetta",
  "Rahim Yar Khan",
  "Rawalpindi",
  "Sahiwal",
  "Sargodha",
  "Sheikhupura",
  "Sialkot",
  "Sukkur",
  "Swat",
  "Tando Allahyar",
  "Tando Muhammad Khan",
  "Thatta",
  "Turbat",
  "Vehari",
  "Wah Cantt",
].sort();

export default function CheckOut() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const { mutate: createOrder, isPending: isOrderLoading } = useCreateOrder();
  const { mutate: validateCoupon, isPending: isValidatingCoupon } = useValidateCoupon();

  // Coupon state: what the customer typed, and what the server accepted
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");

  // Free delivery celebration shows once per visit to checkout
  const [showCelebration, setShowCelebration] = useState(false);
  const celebratedRef = useRef(false);
  const closeCelebration = useCallback(() => setShowCelebration(false), []);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // Step completion states
  const [shippingCompleted, setShippingCompleted] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [reviewCompleted, setReviewCompleted] = useState(false);

  // Cart totals
  const subtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );
  // Delivery is set per product in the dashboard; the cart page adds it up
  // exactly the same way, so the two pages always quote the same total.
  const baseShipping = getCartShipping(cartItems);

  // ✅ Updated validation schema – city is required
  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email address"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9+\-\s()]+$/, "Invalid phone number"),
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("Please select a city"),
    postalCode: Yup.string().optional(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "", // initially empty, user must select
      postalCode: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      handleReviewOrder();
    },
  });

  const freeDelivery = isFreeDeliveryCity(formik.values.city);
  const shipping = freeDelivery ? 0 : baseShipping;
  // Coupons take money off the goods only, never off delivery
  const discount = appliedCoupon ? Math.min(appliedCoupon.discount, subtotal) : 0;
  const total = Math.max(subtotal - discount, 0) + shipping;
  const advancePayment = total * 0.5;
  const remainingPayment = total * 0.5;

  useEffect(() => {
    if (freeDelivery && !celebratedRef.current) {
      celebratedRef.current = true;
      setShowCelebration(true);
    }
  }, [freeDelivery]);

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      setCouponError("Please enter a coupon code");
      return;
    }
    setCouponError("");
    validateCoupon(
      { code, subtotal },
      {
        onSuccess: (data) => {
          setAppliedCoupon({ code: data.code, discount: Number(data.discount) || 0, type: data.discount_type, value: data.discount_value });
          setCouponInput("");
        },
        onError: (err) => {
          setAppliedCoupon(null);
          setCouponError(err.message);
        },
      }
    );
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponError("");
  };

  useEffect(() => {
    const isShippingComplete =
      formik.values.firstName &&
      formik.values.lastName &&
      formik.values.email &&
      formik.values.phone &&
      formik.values.address &&
      formik.values.city &&
      !formik.errors.firstName &&
      !formik.errors.lastName &&
      !formik.errors.email &&
      !formik.errors.phone &&
      !formik.errors.address &&
      !formik.errors.city;
    setShippingCompleted(isShippingComplete);
  }, [formik.values, formik.errors]);

  useEffect(() => {
    setPaymentCompleted(!!uploadedFile);
  }, [uploadedFile]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (validTypes.includes(file.type)) {
        setUploadedFile(file);
        setUploadError("");
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
        } else {
          setPreviewUrl(null);
        }
      } else {
        setUploadError("Please upload PNG, JPG, or JPEG file (PDF not allowed)");
        setUploadedFile(null);
        setPreviewUrl(null);
        e.target.value = "";
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setUploadError("");
    const fileInput = document.getElementById("payment-upload");
    if (fileInput) fileInput.value = "";
  };

  const prepareOrderItems = () => {
    // The chosen option travels inside product_name, so orders, the admin
    // order detail and the confirmation email all show it without any change
    // to the order_items table.
    return cartItems.map(item => ({
      product_name: lineLabel(item.title, item.variantName),
      quantity: item.quantity,
      unit_price: item.price,
    }));
  };

  const submitOrder = async () => {
    const formDataPayload = new FormData();
    const fullName = `${formik.values.firstName} ${formik.values.lastName}`;
    formDataPayload.append("name", fullName);
    formDataPayload.append("email", formik.values.email);
    formDataPayload.append("address", formik.values.address);
    formDataPayload.append("phone", formik.values.phone);
    formDataPayload.append("city", formik.values.city);
    formDataPayload.append("postal_code", formik.values.postalCode);
    formDataPayload.append("items", JSON.stringify(prepareOrderItems()));
    formDataPayload.append("shipping_amount", shipping);
    if (appliedCoupon) formDataPayload.append("coupon_code", appliedCoupon.code);
    formDataPayload.append("payment_screen_short", uploadedFile);

    createOrder(formDataPayload, {
      onSuccess: () => {
        dispatch(clearCart());
        Swal.fire({
          title: "Order Placed!",
          text: "Your order has been successfully placed. Thank you for shopping with us.",
          icon: "success",
          confirmButtonColor: "#0a7e3a",
        }).then(() => {
          navigate("/");
        });
      },
      onError: (error) => {
        // The coupon may have expired or run out since it was applied
        if (appliedCoupon && /coupon/i.test(error?.response?.data?.error || "")) {
          setAppliedCoupon(null);
          setCouponError(error.response.data.error);
        }
        Swal.fire({
          title: "Order Failed",
          text: error?.response?.data?.error || "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      },
    });
  };

  const handleReviewOrder = async () => {
    if (!shippingCompleted) {
      Swal.fire("Incomplete Information", "Please fill in all required shipping details (First Name, Last Name, Email, Phone, Address, City).", "warning");
      return;
    }
    if (!paymentCompleted) {
      Swal.fire("Payment Confirmation Required", "Please upload your payment confirmation.", "warning");
      return;
    }

    // Values come from the customer's own form, so escape before injecting
    // them into the modal markup.
    const esc = (value) =>
      String(value === undefined || value === null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const money = (value) => 'Rs ' + Number(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    const itemRows = cartItems
      .map(
        (item) => `
        <div style="display:flex; gap:12px; align-items:flex-start; padding:12px 0; border-bottom:1px solid #eef0f2;">
          <div style="flex:1; min-width:0;">
            <div style="font-weight:600; color:#191c1d; font-size:14px; line-height:1.35; word-break:break-word;">${esc(lineLabel(item.title, item.variantName))}</div>
            <div style="color:#717786; font-size:12px; margin-top:3px;">${item.quantity} &times; ${money(item.price)}</div>
          </div>
          <div style="font-weight:700; color:#00638a; font-size:14px; white-space:nowrap;">${money(item.price * item.quantity)}</div>
        </div>`
      )
      .join("");

    const addressLine = [formik.values.address, formik.values.city, formik.values.postalCode]
      .filter(Boolean)
      .join(", ");

    let paymentPreviewHtml = "";
    if (uploadedFile) {
      const isImage = uploadedFile.type.startsWith(`image/`);
      const objectUrl = isImage ? URL.createObjectURL(uploadedFile) : null;
      paymentPreviewHtml = `
        <div style="margin-top:18px;">
          <div style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#717786; margin-bottom:8px;">Payment confirmation</div>
          <div style="display:flex; align-items:center; gap:12px; background:#f8f9fa; border:1px solid #e1e3e4; border-radius:12px; padding:10px;">
            ${
              objectUrl
                ? `<img src="${objectUrl}" alt="Payment confirmation" style="width:56px; height:56px; object-fit:cover; border-radius:8px; flex-shrink:0;" />`
                : `<div style="width:56px; height:56px; border-radius:8px; background:#e1e3e4; display:flex; align-items:center; justify-content:center; flex-shrink:0; color:#717786; font-size:11px; font-weight:700;">FILE</div>`
            }
            <div style="min-width:0;">
              <div style="font-size:13px; color:#191c1d; font-weight:600; word-break:break-all;">${esc(uploadedFile.name)}</div>
              <div style="font-size:11px; color:#717786; margin-top:2px;">Attached</div>
            </div>
          </div>
        </div>`;
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    }

    const modalHtml = `
      <div style="text-align:left; max-height:68vh; overflow-y:auto; padding:2px 4px 4px; font-family:Inter, system-ui, sans-serif; color:#191c1d;">

        <!-- Deliver to -->
        <div style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#717786; margin-bottom:8px;">Deliver to</div>
        <div style="background:#f8f9fa; border:1px solid #e1e3e4; border-radius:12px; padding:14px;">
          <div style="font-weight:700; font-size:15px;">${esc(formik.values.firstName)} ${esc(formik.values.lastName)}</div>
          <div style="color:#414754; font-size:13px; margin-top:6px; line-height:1.5; word-break:break-word;">${esc(addressLine)}</div>
          <div style="color:#717786; font-size:12px; margin-top:8px; line-height:1.6; word-break:break-word;">
            ${esc(formik.values.phone)}<br/>${esc(formik.values.email)}
          </div>
        </div>

        <!-- Items -->
        <div style="font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#717786; margin:18px 0 2px;">
          Items (${cartItems.length})
        </div>
        <div>${itemRows}</div>

        <!-- Totals -->
        <div style="margin-top:14px; font-size:13px;">
          <div style="display:flex; justify-content:space-between; padding:5px 0; color:#414754;">
            <span>Subtotal</span><span>${money(subtotal)}</span>
          </div>
          ${discount > 0 ? `
          <div style="display:flex; justify-content:space-between; padding:5px 0; color:#0a7e3a; font-weight:600;">
            <span>Coupon (${esc(appliedCoupon.code)})</span><span>- ${money(discount)}</span>
          </div>` : ""}
          <div style="display:flex; justify-content:space-between; padding:5px 0; color:#414754;">
            <span>Delivery${freeDelivery && baseShipping > 0 ? ` <span style="font-size:11px; color:#0a7e3a; font-weight:700;">(Free in ${esc(formik.values.city)})</span>` : ""}</span><span>${shipping === 0 ? "Free" : money(shipping)}</span>
          </div>
          <div style="display:flex; justify-content:space-between; padding:10px 0 0; margin-top:6px; border-top:1px solid #e1e3e4; font-weight:800; font-size:16px;">
            <span>Total</span><span>${money(total)}</span>
          </div>
        </div>

        <!-- Payment split -->
        <div style="margin-top:16px; border:1px solid #c6e7ff; background:#f2f9ff; border-radius:12px; padding:14px;">
          <div style="display:flex; justify-content:space-between; align-items:baseline;">
            <span style="font-size:13px; font-weight:700; color:#00638a;">Due today (50%)</span>
            <span style="font-size:18px; font-weight:800; color:#00638a;">${money(advancePayment)}</span>
          </div>
          <div style="display:flex; justify-content:space-between; align-items:baseline; margin-top:8px; padding-top:8px; border-top:1px solid #c6e7ff;">
            <span style="font-size:12px; color:#414754;">Remaining on dispatch</span>
            <span style="font-size:13px; font-weight:600; color:#414754;">${money(remainingPayment)}</span>
          </div>
        </div>

        ${paymentPreviewHtml}
      </div>`;
    const result = await Swal.fire({
      title: "Review your order",
      html: modalHtml,
      showCancelButton: true,
      confirmButtonText: "Confirm order",
      cancelButtonText: "Back",
      width: 520,
      padding: '1.25rem',
      reverseButtons: true,
      buttonsStyling: false,
      customClass: {
        popup: "rounded-2xl",
        title: "!text-xl !font-extrabold !text-on-surface !pt-2",
        actions: "w-full flex gap-3 flex-wrap my-4 px-2",
        confirmButton: "flex-1 font-bold py-3 px-6 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors min-w-[120px]",
        cancelButton: "flex-1 font-bold py-3 px-6 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors min-w-[120px]",
      },
    });

    if (result.isConfirmed) {
      setReviewCompleted(true);
      await submitOrder();
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (cartItems.length === 0) {
    return (
      <main className="flex-grow pt-32 pb-20 px-4 md:px-8 max-w-7xl mx-auto w-full text-center">
        <div className="bg-surface-container-lowest rounded-xl p-12">
          <p className="text-on-surface-variant text-lg mb-6">Your cart is empty.</p>
          <Link
            to="/Categories"
            className="inline-flex items-center gap-2 bg-primary text-on-primary font-bold py-3 px-6 rounded-xl hover:opacity-90 transition-all"
          >
            <span className="material-symbols-outlined">shopping_bag</span>
            Continue Shopping
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow pt-32 pb-20 px-4 md:px-8 max-w-7xl mx-auto w-full">
      <WhatsAppButton />
      <FreeDeliveryCelebration
        open={showCelebration}
        city={formik.values.city}
        saved={baseShipping}
        onClose={closeCelebration}
      />
      {/* Stepper */}
      <div className="mb-12 max-w-3xl mx-auto">
        <div className="flex items-center justify-between relative">
          <div className="absolute top-1/2 left-0 w-full h-0.5 bg-surface-container-high -z-10 -translate-y-1/2"></div>
          <div className="flex flex-col items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${shippingCompleted ? "bg-secondary text-on-secondary" : "bg-surface-container-highest text-on-surface-variant"}`}>
              {shippingCompleted ? <span className="material-symbols-outlined text-sm">check</span> : "1"}
            </div>
            <span className={`text-xs font-bold uppercase tracking-widest ${shippingCompleted ? "text-secondary" : "text-on-surface-variant"}`}>Shipping</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${paymentCompleted ? "bg-secondary text-on-secondary" : shippingCompleted ? "bg-primary text-on-primary ring-4 ring-primary-fixed" : "bg-surface-container-highest text-on-surface-variant"}`}>
              {paymentCompleted ? <span className="material-symbols-outlined text-sm">check</span> : "2"}
            </div>
            <span className={`text-xs font-bold uppercase tracking-widest ${paymentCompleted ? "text-secondary" : shippingCompleted ? "text-primary" : "text-on-surface-variant"}`}>Payment</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${reviewCompleted ? "bg-secondary text-on-secondary" : (shippingCompleted && paymentCompleted) ? "bg-primary text-on-primary ring-4 ring-primary-fixed" : "bg-surface-container-highest text-on-surface-variant"}`}>
              {reviewCompleted ? <span className="material-symbols-outlined text-sm">check</span> : "3"}
            </div>
            <span className={`text-xs font-bold uppercase tracking-widest ${reviewCompleted ? "text-secondary" : (shippingCompleted && paymentCompleted) ? "text-primary" : "text-on-surface-variant"}`}>Review</span>
          </div>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-12">
            {/* Billing Details Section */}
            <section className="space-y-6">
              <div className="flex items-center gap-4">
                <span className="text-label-md uppercase tracking-widest text-on-surface-variant text-xs font-bold">Step 02</span>
                <h2 className="text-3xl font-extrabold tracking-tight text-primary">Billing Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-surface-container-low p-8 rounded-xl">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">First Name <span className="text-error">*</span></label>
                  <input type="text" name="firstName" value={formik.values.firstName} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 ${formik.touched.firstName && formik.errors.firstName ? "ring-2 ring-error" : ""}`} placeholder="Enter first name" />
                  {formik.touched.firstName && formik.errors.firstName && <p className="text-error text-xs mt-1">{formik.errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">Last Name <span className="text-error">*</span></label>
                  <input type="text" name="lastName" value={formik.values.lastName} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 ${formik.touched.lastName && formik.errors.lastName ? "ring-2 ring-error" : ""}`} placeholder="Enter last name" />
                  {formik.touched.lastName && formik.errors.lastName && <p className="text-error text-xs mt-1">{formik.errors.lastName}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">Email Address <span className="text-error">*</span></label>
                  <input type="email" name="email" value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 ${formik.touched.email && formik.errors.email ? "ring-2 ring-error" : ""}`} placeholder="you@example.com" />
                  {formik.touched.email && formik.errors.email && <p className="text-error text-xs mt-1">{formik.errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">Phone Number <span className="text-error">*</span></label>
                  <input type="tel" name="phone" value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 ${formik.touched.phone && formik.errors.phone ? "ring-2 ring-error" : ""}`} placeholder="Enter phone number" />
                  {formik.touched.phone && formik.errors.phone && <p className="text-error text-xs mt-1">{formik.errors.phone}</p>}
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">Address <span className="text-error">*</span></label>
                  <input type="text" name="address" value={formik.values.address} onChange={formik.handleChange} onBlur={formik.handleBlur} className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 ${formik.touched.address && formik.errors.address ? "ring-2 ring-error" : ""}`} placeholder="House number and street name" />
                  {formik.touched.address && formik.errors.address && <p className="text-error text-xs mt-1">{formik.errors.address}</p>}
                </div>

                {/* ✅ City Dropdown – replaces text input */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">City <span className="text-error">*</span></label>
                  <select
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full bg-surface-container-highest border-none rounded-sm px-4 py-3 focus:ring-2 focus:ring-primary focus:bg-surface-bright transition-all ${formik.touched.city && formik.errors.city ? "ring-2 ring-error" : ""}`}
                  >
                    <option value="">Select a city</option>
                    {PAKISTAN_CITIES.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                  {formik.touched.city && formik.errors.city && (
                    <p className="text-error text-xs mt-1">{formik.errors.city}</p>
                  )}
                  {freeDelivery ? (
                    <p className="animate-fadeIn flex items-center gap-1 text-xs font-bold text-green-700 mt-1">
                      <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>local_shipping</span>
                      Free delivery unlocked for {formik.values.city}!
                    </p>
                  ) : (
                    <p className="flex items-center gap-1 text-[11px] text-on-surface-variant mt-1">
                      <span className="material-symbols-outlined text-sm">local_shipping</span>
                      Free delivery for Lahore orders
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-on-surface-variant">Postal Code (Optional)</label>
                  <input type="text" name="postalCode" value={formik.values.postalCode} onChange={formik.handleChange} className="w-full bg-surface-container-highest border-none rounded-sm px-4 py-3" placeholder="ZIP / Postal code" />
                </div>
              </div>
            </section>

            {/* Advance Payment Section (unchanged) */}
            <section className="space-y-6">
              <div className="flex items-center gap-4">
                <span className="text-label-md uppercase tracking-widest text-tertiary text-xs font-bold">Mandatory</span>
                <h2 className="text-3xl font-extrabold tracking-tight text-on-surface">Advance Payment</h2>
              </div>
              <div className="bg-tertiary-fixed rounded-xl p-8 border-l-8 border-tertiary shadow-lg shadow-tertiary/5">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1 space-y-4">
                    <h3 className="text-xl font-bold text-on-tertiary-fixed">50% Commitment Required</h3>
                    <p className="text-on-tertiary-fixed-variant leading-relaxed">
                      To finalize your clinical order, a <span className="font-bold">50% advance payment (Rs {advancePayment.toFixed(2)})</span> is required. This ensures priority logistics for medical-grade equipment.
                    </p>
                    <div className="bg-white/40 p-4 rounded-lg space-y-2 text-sm text-on-tertiary-fixed">
                      <p className="font-bold">JazzCash Account Details:</p>
                      <p className="mb-0">Account: 0324 1458166</p>
                      <p>Title: Awais Ali Khan</p>
                    </div>
                  </div>
                  <div className="w-full md:w-64">
                    {!uploadedFile ? (
                      <label className={`h-full border-2 border-dashed ${uploadError ? "border-error" : "border-tertiary/30"} rounded-xl flex flex-col items-center justify-center p-6 text-center bg-white/20 hover:bg-white/40 transition-all cursor-pointer group`}>
                        <input id="payment-upload" type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleFileUpload} className="hidden" />
                        <span className="material-symbols-outlined text-4xl text-tertiary mb-3 group-hover:scale-110 transition-transform">cloud_upload</span>
                        <p className="text-xs font-bold text-on-tertiary-fixed-variant uppercase tracking-tighter">Upload Confirmation</p>
                        <p className="text-[10px] text-on-tertiary-fixed-variant/70 mt-1">PNG, JPG or JPEG</p>
                        {uploadError && <p className="text-error text-xs mt-2">{uploadError}</p>}
                      </label>
                    ) : (
                      <div className="border-2 border-tertiary rounded-xl p-4 bg-white/20">
                        <div className="flex flex-col items-center gap-3">
                          {previewUrl ? (
                            <div className="relative w-full"><img src={previewUrl} alt="Payment confirmation preview" className="w-full h-32 object-cover rounded-lg" /></div>
                          ) : (
                            <div className="w-full h-32 bg-tertiary/10 rounded-lg flex items-center justify-center">
                              <span className="material-symbols-outlined text-4xl text-tertiary">image</span>
                            </div>
                          )}
                          <div className="text-center">
                            <p className="text-xs font-medium text-on-tertiary-fixed-variant truncate max-w-[180px]">{uploadedFile.name}</p>
                            <p className="text-[10px] text-on-tertiary-fixed-variant/70 mt-1">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <button type="button" onClick={handleRemoveFile} className="text-error text-xs font-semibold flex items-center gap-1 hover:opacity-70 transition-opacity">
                            <span className="material-symbols-outlined text-sm">delete</span> Remove
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-center mt-2 text-on-tertiary-fixed-variant">{uploadedFile ? "Confirmation uploaded" : "Required"}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="flex justify-between items-center pt-6 gap-4">
              <Link to="/cart" className="flex-1 text-center bg-surface-container-high text-on-surface font-bold py-4 rounded-xl hover:bg-surface-container-highest transition-colors">
                <span className="material-symbols-outlined align-middle mr-2">arrow_back</span> Back to Cart
              </Link>
              <button
                type="submit"
                disabled={!shippingCompleted || !paymentCompleted || isOrderLoading}
                className={`flex-1 bg-primary text-on-primary py-4 rounded-xl font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all ${(!shippingCompleted || !paymentCompleted || isOrderLoading) ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isOrderLoading ? "Processing..." : "Review Final Order"}
              </button>
            </div>
          </div>

          {/* Order Summary Sidebar (unchanged) */}
          <aside className="lg:col-span-5">
            <div className="sticky top-32 glass-panel p-8 rounded-xl shadow-xl shadow-slate-900/5 space-y-8 border border-white/50">
              <h3 className="text-2xl font-extrabold tracking-tight text-on-surface">Order Summary</h3>
              <div className="space-y-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-20 h-20 bg-surface-container rounded-lg overflow-hidden flex-shrink-0">
                      <img className="w-full h-full object-cover" alt={item.title} src={item.images?.[0] ? process.env.REACT_APP_BE_URL + item.images[0] : "https://via.placeholder.com/400x500?text=No+Image"} />
                    </div>
                    <div className="flex-grow">
                      <h4 className="font-bold text-on-surface">{item.title}</h4>
                      {item.selectedOptions && <p className="text-sm text-on-surface-variant">{item.selectedOptions}</p>}
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs bg-secondary-container text-on-secondary-container px-2 py-0.5 rounded-full font-bold">Qty: {item.quantity}</span>
                        <span className="font-bold text-primary">Rs {(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Coupon */}
              <div className="pt-6 border-t border-on-surface/5">
                {appliedCoupon ? (
                  <div className="animate-fadeIn flex items-center justify-between gap-3 bg-green-50 border border-dashed border-green-400 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="material-symbols-outlined text-green-700" style={{ fontVariationSettings: "'FILL' 1" }}>sell</span>
                      <div className="min-w-0">
                        <p className="font-mono font-bold tracking-wider text-green-800 truncate">{appliedCoupon.code}</p>
                        <p className="text-xs text-green-700">You save Rs {discount.toFixed(2)}</p>
                      </div>
                    </div>
                    <button type="button" onClick={removeCoupon} className="text-xs font-bold text-error hover:opacity-70 shrink-0">Remove</button>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-semibold text-on-surface-variant mb-2">Have a coupon?</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                        className={`flex-1 min-w-0 bg-surface-container-highest border-none rounded-lg px-4 py-3 font-mono tracking-wider uppercase ${couponError ? "ring-2 ring-error" : ""}`}
                        placeholder="Enter code"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={isValidatingCoupon}
                        className="px-5 rounded-lg bg-primary text-on-primary font-bold hover:opacity-90 transition-all disabled:opacity-50"
                      >
                        {isValidatingCoupon ? "..." : "Apply"}
                      </button>
                    </div>
                    {couponError && <p className="text-error text-xs mt-2">{couponError}</p>}
                  </>
                )}
              </div>
              <div className="space-y-4 pt-6 border-t border-on-surface/5">
                <div className="flex justify-between text-on-surface-variant"><span>Subtotal</span><span className="font-medium">Rs {subtotal.toFixed(2)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-700 font-semibold"><span>Coupon ({appliedCoupon.code})</span><span>- Rs {discount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-on-surface-variant">
                  <span className="flex items-center gap-2">
                    Shipping
                    {freeDelivery && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5 rounded-full">{formik.values.city}</span>
                    )}
                  </span>
                  <span className="font-medium">
                    {freeDelivery && baseShipping > 0 && <span className="line-through text-on-surface-variant/60 mr-2">Rs {baseShipping.toFixed(2)}</span>}
                    {shipping === 0 ? <span className={freeDelivery ? "text-green-700 font-bold" : ""}>Free</span> : `Rs ${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between text-xl font-extrabold text-on-surface pt-4 border-t border-on-surface/5"><span>Total</span><span>Rs {total.toFixed(2)}</span></div>
              </div>
              <div className="bg-primary/5 p-4 rounded-lg space-y-2 border border-primary/10">
                <div className="flex justify-between text-sm font-bold text-primary"><span>Due Today (50%)</span><span>Rs {advancePayment.toFixed(2)}</span></div>
                <div className="flex justify-between text-xs text-on-surface-variant"><span>Due upon Dispatch</span><span>Rs {remainingPayment.toFixed(2)}</span></div>
              </div>
            </div>
          </aside>
        </div>
      </form>
    </main>
  );
}